export type ResearchCycleProductStatus =
  | 'requested'
  | 'report_running'
  | 'report_ready'
  | 'panel_running'
  | 'panel_ready'
  | 'result_ready'
  | 'reused'
  | 'failed';

export interface ResearchCycleProductPreflight {
  runId: string;
  current: ResearchCycleProductState | null;
  report: {
    reportId: string;
    seriesId: string;
    window: { from: string; to: string };
    dataHealth: string;
  };
  panel: { modelRef: string; estimatedCostUsd: number };
  preflight: {
    createAllowed: boolean;
    blockers: string[];
    deterministicReport: { executionClass: 'deterministic_non_ai'; createsAiTask: false; chargedUsd: 0 };
    panel: {
      requested: boolean;
      allowed: boolean;
      code: string | null;
      estimatedCostUsd: number;
      taskCreationActivated: boolean;
      aiExecutionActivated: boolean;
    };
    convergence: { status: string; consumedSignalCount: number };
    privacy: { status: string; policyVersion: string | null };
  };
}

export interface ResearchCycleProductState {
  projectId: string;
  runId: string;
  reportId: string;
  status: ResearchCycleProductStatus;
  revision: number;
  estimatedCostUsd: number;
  billingStatus: 'not_started' | 'metering' | 'settled' | 'failed';
  failure: { stage: string; code: string } | null;
  artifactKinds: Array<'report' | 'panel' | 'result' | 'strategy_reuse'>;
  canRetry: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchCycleStrategyReuseRequest {
  strategyId: string;
  strategyVersionId: string;
  registryExperimentId: string;
  releaseId: string;
}

export type ResearchCycleReuseProvenance = {
  strategyRef: string;
  strategyVersionRef: string;
  reportRef: string;
  panelRef: string;
  evidenceManifestRef: string;
  calibrationRef: string;
  causalGraphRef: string;
  metricSetRef: string;
  promptRef: string;
  modelRef: string;
  freshnessRef: string;
  releaseLearningRef: string;
};

export interface ResearchCycleStrategyReuseProjection {
  schemaVersion: 1;
  runId: string;
  reportId: string;
  status: 'result_ready' | 'reused';
  revision: number;
  reuseAllowed: boolean;
  blockers: string[];
  strategy: {
    strategyId: string;
    strategyVersionId: string;
    certificationStatus: 'certified' | 'limited';
  };
  releaseLearning: {
    releaseId: string;
    state: 'prepared' | 'publishing' | 'published' | 'collecting' | 'evaluating' | 'evaluated' | 'acted_on' | 'failed';
    authoritative: boolean;
  };
  provenance: ResearchCycleReuseProvenance;
  reuseId: string;
  alreadyReused: boolean;
}

const STATUS = new Set<ResearchCycleProductStatus>([
  'requested', 'report_running', 'report_ready', 'panel_running', 'panel_ready',
  'result_ready', 'reused', 'failed',
]);
const BILLING = new Set(['not_started', 'metering', 'settled', 'failed']);
const ARTIFACTS = new Set(['report', 'panel', 'result', 'strategy_reuse']);
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9._:@+-]{0,175}$/;
const REUSE_STATUS = new Set(['result_ready', 'reused']);
const CERTIFICATION = new Set(['certified', 'limited']);
const RELEASE_STATE = new Set(['prepared', 'publishing', 'published', 'collecting', 'evaluating', 'evaluated', 'acted_on', 'failed']);
const REUSE_PIN_KEYS = [
  'strategyRef', 'strategyVersionRef', 'reportRef', 'panelRef', 'evidenceManifestRef',
  'calibrationRef', 'causalGraphRef', 'metricSetRef', 'promptRef', 'modelRef',
  'freshnessRef', 'releaseLearningRef',
] as const;

export function parseResearchCycleStrategyReuseProjection(
  value: unknown,
  expected: { runId: string; strategyId: string; strategyVersionId: string; releaseId: string },
): ResearchCycleStrategyReuseProjection {
  rejectPrivateShape(value);
  const root = exactRecord(value, [
    'schemaVersion', 'runId', 'reportId', 'status', 'revision', 'reuseAllowed', 'blockers',
    'strategy', 'releaseLearning', 'provenance', 'reuseId', 'alreadyReused',
  ], 'reuse');
  if (root.schemaVersion !== 1) throw new Error('Research Cycle reuse schema version is unsupported');
  const runId = safeString(root.runId, 'runId');
  if (runId !== expected.runId) throw new Error('Research Cycle reuse run scope mismatch');
  const status = safeString(root.status, 'status');
  if (!REUSE_STATUS.has(status)) throw new Error('Research Cycle reuse status is invalid');
  const blockers = stringArray(root.blockers, 'blockers');
  const reuseAllowed = booleanValue(root.reuseAllowed, 'reuseAllowed');
  if (reuseAllowed === (blockers.length > 0)) throw new Error('Research Cycle reuse blocker decision is inconsistent');

  const strategy = exactRecord(
    root.strategy,
    ['strategyId', 'strategyVersionId', 'certificationStatus'],
    'reuse.strategy',
  );
  const strategyId = safeString(strategy.strategyId, 'strategyId');
  const strategyVersionId = safeString(strategy.strategyVersionId, 'strategyVersionId');
  const certificationStatus = safeString(strategy.certificationStatus, 'certificationStatus');
  if (strategyId !== expected.strategyId || strategyVersionId !== expected.strategyVersionId) {
    throw new Error('Research Cycle reuse strategy scope mismatch');
  }
  if (!CERTIFICATION.has(certificationStatus)) throw new Error('Research Cycle certification status is invalid');

  const release = exactRecord(
    root.releaseLearning,
    ['releaseId', 'state', 'authoritative'],
    'reuse.releaseLearning',
  );
  const releaseId = safeString(release.releaseId, 'releaseId');
  const releaseState = safeString(release.state, 'releaseLearning.state');
  if (releaseId !== expected.releaseId) throw new Error('Research Cycle release-learning scope mismatch');
  if (!RELEASE_STATE.has(releaseState)) throw new Error('Research Cycle release-learning state is invalid');

  const provenanceRecord = exactRecord(root.provenance, [...REUSE_PIN_KEYS], 'reuse.provenance');
  const provenance = Object.fromEntries(REUSE_PIN_KEYS.map((key) => [
    key,
    safeString(provenanceRecord[key], `provenance.${key}`),
  ])) as ResearchCycleReuseProvenance;
  if (provenance.strategyRef !== `strategy:${strategyId}`
    || provenance.strategyVersionRef !== `strategy-version:${strategyVersionId}`) {
    throw new Error('Research Cycle reuse strategy provenance is inconsistent');
  }

  const alreadyReused = booleanValue(root.alreadyReused, 'alreadyReused');
  if (alreadyReused !== (status === 'reused')) {
    throw new Error('Research Cycle reuse terminal state is inconsistent');
  }
  return {
    schemaVersion: 1,
    runId,
    reportId: safeString(root.reportId, 'reportId'),
    status: status as ResearchCycleStrategyReuseProjection['status'],
    revision: positiveInteger(root.revision, 'revision'),
    reuseAllowed,
    blockers,
    strategy: {
      strategyId,
      strategyVersionId,
      certificationStatus: certificationStatus as 'certified' | 'limited',
    },
    releaseLearning: {
      releaseId,
      state: releaseState as ResearchCycleStrategyReuseProjection['releaseLearning']['state'],
      authoritative: booleanValue(release.authoritative, 'releaseLearning.authoritative'),
    },
    provenance,
    reuseId: safeString(root.reuseId, 'reuseId'),
    alreadyReused,
  };
}

export function parseResearchCycleProductPreflight(
  value: unknown,
  expected: { projectId: string; reportId: string },
): ResearchCycleProductPreflight {
  rejectPrivateShape(value);
  const root = record(value, 'preflight');
  const report = record(root.report, 'preflight.report');
  const window = record(report.window, 'preflight.report.window');
  const panel = record(root.panel, 'preflight.panel');
  const preflight = record(root.preflight, 'preflight.preflight');
  const deterministic = record(preflight.deterministicReport, 'preflight.deterministicReport');
  const panelDecision = record(preflight.panel, 'preflight.panelDecision');
  const convergence = record(preflight.convergence, 'preflight.convergence');
  const privacy = record(preflight.privacy, 'preflight.privacy');
  const reportId = safeString(report.reportId, 'reportId');
  if (reportId !== expected.reportId) throw new Error('Research Cycle preflight report scope mismatch');
  if (
    deterministic.executionClass !== 'deterministic_non_ai'
    || deterministic.createsAiTask !== false
    || deterministic.chargedUsd !== 0
  ) throw new Error('Research Cycle deterministic report contract is invalid');

  return {
    runId: safeString(root.runId, 'runId'),
    current: root.current === null
      ? null
      : parseResearchCycleProductState(root.current, {
          projectId: expected.projectId,
          reportId,
          runId: safeString(root.runId, 'runId'),
        }),
    report: {
      reportId,
      seriesId: safeString(report.seriesId, 'seriesId'),
      window: { from: iso(window.from, 'from'), to: iso(window.to, 'to') },
      dataHealth: safeString(report.dataHealth, 'dataHealth'),
    },
    panel: {
      modelRef: safeString(panel.modelRef, 'modelRef'),
      estimatedCostUsd: nonNegative(panel.estimatedCostUsd, 'estimatedCostUsd'),
    },
    preflight: {
      createAllowed: booleanValue(preflight.createAllowed, 'createAllowed'),
      blockers: stringArray(preflight.blockers, 'blockers'),
      deterministicReport: {
        executionClass: 'deterministic_non_ai', createsAiTask: false, chargedUsd: 0,
      },
      panel: {
        requested: booleanValue(panelDecision.requested, 'panel.requested'),
        allowed: booleanValue(panelDecision.allowed, 'panel.allowed'),
        code: nullableString(panelDecision.code, 'panel.code'),
        estimatedCostUsd: nonNegative(panelDecision.estimatedCostUsd, 'panel.estimatedCostUsd'),
        taskCreationActivated: booleanValue(panelDecision.taskCreationActivated, 'panel.taskCreationActivated'),
        aiExecutionActivated: booleanValue(panelDecision.aiExecutionActivated, 'panel.aiExecutionActivated'),
      },
      convergence: {
        status: safeString(convergence.status, 'convergence.status'),
        consumedSignalCount: nonNegativeInteger(convergence.consumedSignalCount, 'convergence.consumedSignalCount'),
      },
      privacy: {
        status: safeString(privacy.status, 'privacy.status'),
        policyVersion: nullableString(privacy.policyVersion, 'privacy.policyVersion'),
      },
    },
  };
}

export function parseResearchCycleProductState(
  value: unknown,
  expected: { projectId: string; reportId?: string; runId?: string },
): ResearchCycleProductState {
  rejectPrivateShape(value);
  const root = record(value, 'state');
  const projectId = safeString(root.projectId, 'projectId');
  const runId = safeString(root.runId, 'runId');
  const reportId = safeString(root.reportId, 'reportId');
  if (projectId !== expected.projectId || (expected.reportId && reportId !== expected.reportId) || (expected.runId && runId !== expected.runId)) {
    throw new Error('Research Cycle state scope mismatch');
  }
  const status = safeString(root.status, 'status') as ResearchCycleProductStatus;
  if (!STATUS.has(status)) throw new Error('Research Cycle status is invalid');
  const billingStatus = safeString(root.billingStatus, 'billingStatus');
  if (!BILLING.has(billingStatus)) throw new Error('Research Cycle billing status is invalid');
  const artifactKinds = stringArray(root.artifactKinds, 'artifactKinds');
  if (artifactKinds.some((kind) => !ARTIFACTS.has(kind))) throw new Error('Research Cycle artifact kind is invalid');
  const failureRecord = root.failure === null ? null : record(root.failure, 'failure');
  return {
    projectId,
    runId,
    reportId,
    status,
    revision: positiveInteger(root.revision, 'revision'),
    estimatedCostUsd: nonNegative(root.estimatedCostUsd, 'estimatedCostUsd'),
    billingStatus: billingStatus as ResearchCycleProductState['billingStatus'],
    failure: failureRecord ? {
      stage: safeString(failureRecord.stage, 'failure.stage'),
      code: safeString(failureRecord.code, 'failure.code'),
    } : null,
    artifactKinds: artifactKinds as ResearchCycleProductState['artifactKinds'],
    canRetry: booleanValue(root.canRetry, 'canRetry'),
    createdAt: iso(root.createdAt, 'createdAt'),
    updatedAt: iso(root.updatedAt, 'updatedAt'),
  };
}

function rejectPrivateShape(value: unknown): void {
  const serialized = JSON.stringify(value);
  if (/privateArtifactRef|replayBundlePath|promptRefs|activePanelTaskId|panelTaskIds|gs:\/\/|\/private\/|research\/|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|authorization|access[_-]?token|refresh[_-]?token|api[_-]?key|password|secret/i.test(serialized)) {
    throw new Error('Research Cycle response contains a private or internal field');
  }
}

function record(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${field} must be an object`);
  return value as Record<string, unknown>;
}

function exactRecord(value: unknown, keys: string[], field: string): Record<string, unknown> {
  const result = record(value, field);
  if (JSON.stringify(Object.keys(result).sort()) !== JSON.stringify([...keys].sort())) {
    throw new Error(`${field} contains unsupported fields`);
  }
  return result;
}

function safeString(value: unknown, field: string): string {
  if (typeof value !== 'string' || !SAFE_ID.test(value)) throw new Error(`${field} must be a safe identifier`);
  return value;
}

function nullableString(value: unknown, field: string): string | null {
  return value === null ? null : safeString(value, field);
}

function booleanValue(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`${field} must be boolean`);
  return value;
}

function nonNegative(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new Error(`${field} must be non-negative`);
  return value;
}

function nonNegativeInteger(value: unknown, field: string): number {
  const result = nonNegative(value, field);
  if (!Number.isInteger(result)) throw new Error(`${field} must be an integer`);
  return result;
}

function positiveInteger(value: unknown, field: string): number {
  const result = nonNegativeInteger(value, field);
  if (result < 1) throw new Error(`${field} must be positive`);
  return result;
}

function stringArray(value: unknown, field: string): string[] {
  if (!Array.isArray(value) || value.length > 32) throw new Error(`${field} must be a bounded array`);
  return value.map((entry, index) => safeString(entry, `${field}[${index}]`));
}

function iso(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length > 40 || !Number.isFinite(Date.parse(value))) {
    throw new Error(`${field} must be an ISO timestamp`);
  }
  return value;
}
